import { Input } from '@/components/ui';
import { Spinner } from '@/components/Common';
import { CACHE_TIME } from '@/constants';
import { trpc } from '@/data-loaders';
import { useHandleError, useHandleSuccess, useSchoolList } from '@/hooks';
import { useAwmsStore } from '@/store';
import { Button } from '@curtin-dxp/curtin-ui';
import { CircleCheckBig, Pencil } from 'lucide-react';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { SelectDropDown } from '@/components/Common/SelectDropDown/SelectDropDown';
import { toast } from 'sonner';

export const UpdateStaffDetails = () => {
  const [enteredCurtinId, setEnteredCurtinId] = useState('');
  const [curtinIdToValidate, setCurtinIdToValidate] = useState('');
  const [validateTriggerCount, setValidateTriggerCount] = useState(0);
  const [validId, setValidId] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const sendToast = useHandleSuccess({ title: 'Update staff details' });
  const handleError = useHandleError();
  const lastErrorRef = useRef<any>(null);
  const [hasValidated, setHasValidated] = useState(false);

  const activeYear = useAwmsStore((s) => s.activeYear);
  const trpcUtils = trpc.useUtils();

  const {
    data: staffDetails,
    refetch,
    error: staffDetailsError,
    isFetching,
  } = trpc.admin.getStaffMemberDetails.useQuery(
    { curtinId: curtinIdToValidate, year: activeYear },
    {
      enabled: validId,
      retry: false,
      cacheTime: CACHE_TIME,
    }
  );

  const { mutate: updateStaffDetails, isLoading } = trpc.admin.updateStaffMemberDetails.useMutation({
    onSuccess: () => {
      sendToast();
      setFormVisible(false);
      setHasValidated(false);
      trpcUtils.invalidate();
    },
    onError: (error) => handleError([error]),
  });

  const { data: schoolList = [] } = useSchoolList(activeYear);

  const { data: workFunctions = [] } = trpc.admin.getWorkFunctionList.useQuery(undefined, {
    cacheTime: CACHE_TIME,
  });

  const [schoolId, setSchoolId] = useState<number | undefined>();
  const [discipline, setDiscipline] = useState('');
  const [fte, setFte] = useState('');
  const [workFunction, setWorkFunction] = useState('');

  const { data: disciplines = [] } = trpc.admin.getDisciplinesBySchool.useQuery(
    { schoolId: schoolId! },
    {
      enabled: !!schoolId,
      cacheTime: CACHE_TIME,
    },
  );

  const workFunctionList =
    workFunctions?.map((wf, idx) => ({
      id: idx,
      name: wf.name,
    })) ?? [];

  // 1. Set initial staff details
  useEffect(() => {
    if (staffDetails && hasValidated) {
      setValidId(true);
      setFormVisible(true);
      setSchoolId(staffDetails.schoolId ?? undefined);
      setFte(staffDetails.fte?.toString() ?? '');
      setWorkFunction(staffDetails.workFunction ?? '');
    }
  }, [staffDetails, hasValidated]);

  // 2. Set discipline only when discipline list is ready
  useEffect(() => {
    if (staffDetails?.discipline && disciplines.length > 0 && !discipline) {
      const match = disciplines.find((d) => d.id === String(staffDetails.discipline));
      if (match) setDiscipline(String(match.id));
    }
  }, [disciplines, staffDetails, discipline]);

  useEffect(() => {
    if (staffDetailsError && lastErrorRef.current !== staffDetailsError) {
      handleError([staffDetailsError]);
      lastErrorRef.current = staffDetailsError;
    }
  }, [staffDetailsError]);

  useEffect(() => {
    if (curtinIdToValidate) refetch();
  }, [curtinIdToValidate, validateTriggerCount]);

  const handleCurtinIdChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEnteredCurtinId(e.target.value);
    setValidId(false);
    setFormVisible(false);
    setCurtinIdToValidate('');
    setSchoolId(undefined);
    setDiscipline('');
    setFte('');
    setWorkFunction('');
  };

  const handleValidate = () => {
    if (!enteredCurtinId.trim()) return;
    setHasValidated(true);
    setCurtinIdToValidate(enteredCurtinId);
    setValidateTriggerCount((prev) => prev + 1);
  };

  const handleUpdate = () => {
    if (!validId || !schoolId || !fte || !workFunction) return;

    const fteValue = parseFloat(fte);
    if (isNaN(fteValue)) {
      toast.error('FTE must be a valid number.');
      return;
    }

    updateStaffDetails({
      curtinId: curtinIdToValidate,
      year: activeYear,
      schoolId,
      discipline: discipline === '' ? null : discipline,
      fte: fteValue,
      workFunction,
    });
  };

  return (
    <div className="ml-8 mt-10 max-w-7xl">
      {/* Curtin ID Validation */}
      <div className="flex w-[400px] gap-4 items-center">
        <div className="relative">
          <Input
            type="text"
            value={enteredCurtinId}
            className="w-[180px] border-secondary-grey-04 pr-10 text-b2"
            onChange={handleCurtinIdChange}
            placeholder="Enter Curtin ID"
          />
          {validId && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-green-600">✔️</span>}
        </div>
        <Button
          variant="outline-secondary"
          className="h-10 border-black bg-black text-white hover:bg-transparent hover:text-black focus:outline-none focus-visible:ring-1
            focus-visible:ring-inset focus-visible:ring-white focus-visible:ring-offset-0"
          aria-label="Validate Curtin ID"
          onClick={handleValidate}
        >
          Retrieve staff details
          <CircleCheckBig className="ml-[10px] h-6 w-6" />
        </Button>
      </div>

      {isFetching && (
        <div className="mt-4">
          <Spinner />
        </div>
      )}

      {/* Display current details in a summary table */}
      {staffDetails && enteredCurtinId && (
        <div className="mt-6 rounded-md border border-gray-300 bg-white">
          <div className="border-b border-gray-300 bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700">
            Current Staff Details
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse text-sm">
              <thead className="bg-gray-100 text-left text-gray-700">
                <tr>
                  <th className="w-1/6 border border-gray-300 px-4 py-2">Curtin ID</th>
                  <th className="w-1/5 border border-gray-300 px-4 py-2">Name</th>
                  <th className="w-1/5 border border-gray-300 px-4 py-2">School</th>
                  <th className="w-1/6 border border-gray-300 px-4 py-2">Discipline</th>
                  <th className="w-1/4 border border-gray-300 px-4 py-2">Work Function</th>
                  <th className="w-[80px] border border-gray-300 px-4 py-2 text-right">FTE</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t">
                  <td className="border border-gray-300 px-4 py-3">{staffDetails.curtinId}</td>
                  <td className="border border-gray-300 px-4 py-3">
                    {staffDetails.lastName}
                    {staffDetails.firstName ? `, ${staffDetails.firstName}` : ''}
                  </td>
                  <td className="border border-gray-300 px-4 py-3">
                    {schoolList.find((s) => s.id === staffDetails.schoolId)?.name ?? '—'}
                  </td>
                  <td className="border border-gray-300 px-4 py-3">
                    {disciplines.find((d) => d.id === String(staffDetails.discipline))?.name ?? '—'}
                  </td>
                  <td className="border border-gray-300 px-4 py-3">{staffDetails.workFunction ?? '—'}</td>
                  <td className="border border-gray-300 px-4 py-3 text-right">{staffDetails.fte ?? '—'}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {formVisible && validId && (
        <div className="mt-8 flex flex-col gap-4">
         {/* School Dropdown */}
          <div className="max-w-xl">
            <label className="text-sm font-medium">School</label>
            <SelectDropDown
              list={schoolList}
              placeholder="Select school"
              initialValue={schoolId}
              valueChangeCallback={(val: number) => {
                setSchoolId(val);
                setDiscipline('');
              }}
            />
          </div>

         {/* Discipline Dropdown */}
          {schoolId && (
            <div className="max-w-xl">
              <label className="text-sm font-medium">Discipline</label>
              <SelectDropDown
                key={discipline}
                list={disciplines.map((d) => ({ id: Number(d.id), name: d.name }))}
                placeholder={disciplines.length === 0 ? 'No disciplines available' : 'Select discipline'}
                initialValue={discipline ? Number(discipline) : undefined}
                valueChangeCallback={(val: number) => setDiscipline(String(val))}
                disabled={disciplines.length === 0}
              />
            </div>
          )}

          {/* FTE Input */}
          {schoolId && (
            <div className="max-w-xl">
              <label className="text-sm font-medium">FTE</label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max="1"
                value={fte}
                onChange={(e) => {
                  const val = parseFloat(e.target.value);
                  if (e.target.value === '' || (val <= 1 && /^\d*\.?\d{0,2}$/.test(e.target.value))) {
                    setFte(e.target.value);
                  }
                }}
                placeholder="e.g. 1.0"
              />
            </div>
          )}

          {/* Work Function Dropdown */}
          {fte && (
            <div className="max-w-xl">
              <label className="text-sm font-medium">Work Function</label>
              <SelectDropDown
                list={workFunctionList}
                placeholder="Select work function"
                initialValue={workFunctionList.find((wf) => wf.name === workFunction)?.id}
                valueChangeCallback={(val: number) => {
                  const selected = workFunctionList.find((wf) => wf.id === val);
                  setWorkFunction(selected?.name ?? '');
                }}
              />
            </div>
          )}

          {/* Submit Button */}
          {workFunction && (
            <div className="mt-8">
              <Button
                variant="outline-secondary"
                onClick={handleUpdate}
                className="h-10 border-black bg-black text-white hover:bg-transparent hover:text-black focus:outline-none focus-visible:ring-1
                  focus-visible:ring-inset focus-visible:ring-white focus-visible:ring-offset-0"
                disabled={isLoading}
              >
                {isLoading ? 'Updating...' : 'Update Staff Details'}
                <Pencil className="ml-[10px] h-6 w-6" />
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
